import React, { useContext, useState } from 'react'
import {
  Alert,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
} from '@mui/material'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
import { VehicleContext } from '../SingleVehicle'
import { SectionHeader } from './SectionKit'

const toNumberOrNull = (value) =>
  value === '' || value === null || value === undefined ? null : Number(value)

// PTS planning reads this configuration when routing journeys onto duties —
// capacity counts feed the route planner's warnings (and, later, blocking).
const PTSConfigSection = () => {
  const { vehicle, loadVehicle } = useContext(VehicleContext)
  const { setToast } = useContext(GlobalContext)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)

  const capacity = vehicle?.ptsCapacity || {}
  const [form, setForm] = useState({
    walking: capacity.walking ?? '',
    wheelchair: capacity.wheelchair ?? '',
    stretcher: capacity.stretcher ?? '',
    maxPatients: capacity.maxPatients ?? '',
    escortSeats: capacity.escortSeats ?? '',
    wheelchairRestraints: capacity.wheelchairRestraints ?? '',
    maxPatientWeightKg: capacity.maxPatientWeightKg ?? '',
    notes: capacity.notes ?? '',
    bariatric: capacity.bariatric ?? false,
    hasTailLift: vehicle?.hasTailLift ?? false,
    isC1: vehicle?.isC1 ?? false,
    hasBlueLights: vehicle?.hasBlueLights ?? false,
  })

  const setField = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }))
  const setToggle = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.checked }))

  const save = async () => {
    setSaving(true)
    setError(false)
    try {
      const res = await API.put(`/edit-vehicle?vehicle=${vehicle._id}`, {
        hasTailLift: form.hasTailLift,
        isC1: form.isC1,
        hasBlueLights: form.hasBlueLights,
        ptsCapacity: {
          walking: toNumberOrNull(form.walking),
          wheelchair: toNumberOrNull(form.wheelchair),
          stretcher: toNumberOrNull(form.stretcher),
          bariatric: form.bariatric,
          maxPatients: toNumberOrNull(form.maxPatients),
          escortSeats: toNumberOrNull(form.escortSeats),
          wheelchairRestraints: toNumberOrNull(form.wheelchairRestraints),
          maxPatientWeightKg: toNumberOrNull(form.maxPatientWeightKg),
          notes: form.notes,
        },
      })
      if (res.status === 200) {
        setToast({ message: 'PTS configuration updated' })
        loadVehicle()
      } else {
        setError(true)
      }
    } catch (e) {
      setError(true)
    } finally {
      setSaving(false)
    }
  }

  const capacitySummary = ['walking', 'wheelchair', 'stretcher']
    .map((key) =>
      capacity[key] !== undefined && capacity[key] !== null
        ? `${capacity[key]} ${key}`
        : null,
    )
    .filter(Boolean)
    .join(' · ')

  return (
    <Stack spacing={2}>
      <SectionHeader
        title="PTS Configuration"
        caption="Patient capacity is used by PTS journey planning to warn when a route exceeds what this vehicle can carry"
        chip={
          capacitySummary ? (
            <Chip
              size="small"
              label={capacitySummary}
              sx={{ borderRadius: 1 }}
            />
          ) : (
            <Chip
              size="small"
              label="Capacity not set"
              color="warning"
              sx={{ borderRadius: 1 }}
            />
          )
        }
      />
      <Grid container spacing={2}>
        {[
          ['walking', 'Seated / walking patients'],
          ['wheelchair', 'Wheelchair positions'],
          ['stretcher', 'Stretcher positions'],
          ['maxPatients', 'Max patients (any mix)'],
          ['escortSeats', 'Escort seats'],
          ['wheelchairRestraints', 'Wheelchair restraints'],
          ['maxPatientWeightKg', 'Max patient weight (kg)'],
        ].map(([field, label]) => (
          <Grid item xs={12} md={6} lg={4} xl={3} key={field}>
            <TextField
              label={label}
              type="number"
              size="small"
              fullWidth
              value={form[field]}
              onChange={setField(field)}
              inputProps={{ min: 0 }}
            />
          </Grid>
        ))}
        <Grid item xs={12} md={8}>
          <TextField
            label="Capacity notes"
            size="small"
            fullWidth
            multiline
            value={form.notes}
            onChange={setField('notes')}
            placeholder="e.g. Stretcher blocks two seated positions"
          />
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" flexWrap="wrap" useFlexGap columnGap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.bariatric}
                  onChange={setToggle('bariatric')}
                />
              }
              label="Bariatric capable"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.hasTailLift}
                  onChange={setToggle('hasTailLift')}
                />
              }
              label="Tail lift"
            />
            <FormControlLabel
              control={
                <Switch checked={form.isC1} onChange={setToggle('isC1')} />
              }
              label="C1 licence required"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.hasBlueLights}
                  onChange={setToggle('hasBlueLights')}
                />
              }
              label="Blue lights"
            />
          </Stack>
        </Grid>
        {error && (
          <Grid item xs={12}>
            <Alert severity="error">
              Unable to save the PTS configuration. Please try again.
            </Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end">
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? 'Saving...' : 'Save PTS Configuration'}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  )
}

export default PTSConfigSection
